import React, { useEffect, useState } from "react";
import Typography from "@material-ui/core/Typography";
import {
  CssBaseline,
  GridList,
  Slider,
  GridListTile,
  useTheme,
} from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import ArrowRightAltIcon from "@material-ui/icons/ArrowRightAlt";
import SearchIcon from "@material-ui/icons/Search";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ImageMagnifier from "../common/ImageMagnifier";
import malePatient_img from "../../images/male_body.png";
import femalePatient_img from "../../images/female_body.png";
import search_img from "../../images/search_icon.png";
import { v4 as uuidv4 } from "uuid";
import {
  CategoryButton,
  GameModalLayout,
  CustomLabel,
  useWindowDimensions,
} from "../common";
import { GAME_STAGE } from "../../utils/constant";
import { history } from "../../index";
import { checkGameStatus } from "../../service/logic";
import { fetchExamination } from "../../service/logic";
import { addGameActivity } from "../../redux/dispatchers";
import Typewriter from "typewriter-effect";
import { AnswerLinkPopup } from "../common/answer-link-popup";
import Button from "@material-ui/core/Button";
import { getOptions } from "../../service/api/option";
import { useExaminationStyles } from "../../styles/examination-style";
import { store } from "../../redux/store";

export default function Examination() {
  const classes = useExaminationStyles();
  const theme = useTheme();
  const isWidthSm = useMediaQuery(theme.breakpoints.down("xs"));
  const gender = store.getState().gameStates.case.gender;
  const [parts, setParts] = useState([]);
  const [search, setSearch] = useState("");
  const [sign, setSign] = useState("");
  const [answer, setAnswer] = useState("");
  const [link, setLink] = useState([]);
  const [selectedSystem, setSelectedSystem] = useState({ id: "" });
  const [systemList, setSystemList] = useState([]);
  const [selectedPart, setSelectedPart] = useState({
    id: "",
    name: "",
  });
  const [selectedSubsystem, setSelectedSubsystem] = useState({
    id: "",
  });
  const [magnifyRate, setMagnifyRate] = useState(2);
  const { height, width } = useWindowDimensions();
  const [general, setGeneral] = useState(null);
  const [vital, setVital] = useState(null);
  const handleMagnifyRateChange = (event, newValue) => {
    setMagnifyRate(newValue);
  };

  const sliderMarks = [
    {
      value: 2,
      label: "x2",
    },
    {
      value: 3,
      label: "x3",
    },
    {
      value: 4,
      label: "x4",
    },
    {
      value: 5,
      label: "x5",
    },
  ];

  const renderAnswer = () => {
    const currentSigns = answer.slice(0, 3);
    const nextSigns = answer.slice(3);

    if (typeof answer == "string") {
      return (
        <Typewriter
          options={{
            cursor: null,
            strings: answer,
            autoStart: true,
            delay: 18,
          }}
        />
      );
    }

    return (
      <div className={classes.suggestionContainer}>
        <Typography>Are you looking for sign</Typography>
        {currentSigns.map((record) => (
          <Typography
            key={record.id}
            className={classes.suggestion}
            onClick={() => {
              setAnswer(record.answer.contents);
              setLink(record.answer.link);
              addGameActivity("examination", record.id);
            }}
          >
            {record.sign}
          </Typography>
        ))}
        <Typography
          className={classes.suggestion}
          onClick={() => {
            if (nextSigns.length) {
              setAnswer(nextSigns);
            } else {
              setAnswer("No / Absent.");
              setLink([]);
            }
          }}
        >
          None of above
        </Typography>
      </div>
    );
  };

  // helper function to identify examination with subsystems
  const systemsToLocalSystems = (systems) => {
    const tmp = systems.filter((s) => s.dependent === "");
    // find all the subsystems
    tmp.forEach((x) => {
      x.subsystems = systems
        .filter((s) => s.dependent === x.id)
        .map((sub) => ({
          id: uuidv4(),
          optionId: sub.id,
          name: sub.name,
        }));
    });
    // reformat all the systems
    return tmp.map((t) => ({
      id: uuidv4(),
      optionId: t.id,
      name: t.name,
      subsystems: t.subsystems,
    }));
  };

  // update selected part if system or subsystem has changed
  useEffect(() => {
    if (!selectedSystem || !selectedSystem.optionId) return;
    let tmp = selectedSystem.optionId;
    if (!!selectedSubsystem.optionId) tmp = selectedSubsystem.optionId;
    // filter parts based on selected system
    getOptions("examType", tmp).then((result) => {
      setParts(
        result.filter(
          (r) => !r.id.includes("GeneralAppearance") && !r.id.includes("Vital")
        )
      );
      setGeneral(result.find((r) => r.id.includes("GeneralAppearance")));
      setVital(result.find((r) => r.id.includes("Vital")));
    });
  }, [selectedSystem, selectedSubsystem]);

  // initialize system list
  useEffect(() => {
    getOptions("examSystem").then((systems) => {
      setSystemList(systemsToLocalSystems(systems));
    });

    // check if user is allowed to enter examination page
    if (!checkGameStatus(GAME_STAGE.EXAMINATION)) {
      // if not allowed, navigate to case list page
      history.push("/cases");
    }
  }, []);

  const onAsk = async () => {
    setAnswer("");

    if (!sign) {
      // empty question
      setAnswer("Please enter signs!");
      setLink([]);
      return;
    }

    // identify system id
    let tmp = selectedSystem.optionId;
    if (!!selectedSubsystem.optionId) tmp = selectedSubsystem.optionId;

    // fetch result
    const result = await fetchExamination(tmp, selectedPart.id, sign);

    // if no result found, set answer to none.
    if (!result) {
      setAnswer("No / Absent.");
      setLink([]);

      return;
    }

    if (result.length === 1) {
      // else add into game activity and display to the users.
      const { answer, id, defaultAnswer } = result[0];

      setAnswer(
        !!answer.link && answer.link.length > 0
          ? answer.contents || ""
          : answer.contents || defaultAnswer || "No / Absent."
      );
      setLink(answer.link);
      addGameActivity("examination", id);
    } else {
      setAnswer(result);
      setLink([]);
    }
  };

  const reset = (type) => {
    if (type === "system") {
      setSelectedSystem({
        id: "",
      });
      setSelectedSubsystem({
        id: "",
      });
    } else {
      if (type === "subsystem")
        setSelectedSubsystem({
          id: "",
        });
    }
    setSelectedPart({
      id: "",
      name: "",
    });
    setSign("");
    setAnswer("");
    setLink([]);
  };

  const pickLabelArr = (level) => {
    switch (level) {
      case "subsystem":
        return [
          {
            id: selectedSystem.id,
            name: selectedSystem.name,
            onClick: () => reset("system"),
          },
          {
            id: selectedSubsystem.id,
            name: selectedSubsystem.name,
            onClick: () => reset("subsystem"),
          },
        ];
      case "partWithSubsystem":
        return [
          {
            id: selectedSubsystem.id,
            name: selectedSubsystem.name,
            onClick: () => reset("subsystem"),
          },
          {
            id: selectedPart.id,
            name: selectedPart.name,
            onClick: () => reset("part"),
          },
        ];
      case "partWithoutSubsystem":
        return [
          {
            id: selectedSubsystem.id,
            name: selectedSystem.name,
            onClick: () => reset("system"),
          },
          {
            id: selectedPart.id,
            name: selectedPart.name,
            onClick: () => reset("part"),
          },
        ];
      default:
        return;
    }
  };

  // render the right modal
  const renderRightContent = () => {
    // if no system is selected yet, ask user to select system
    if (!selectedSystem.id.length) {
      return (
        <Typography variant="h2" className={classes.title}>
          Select system to be examined
        </Typography>
      );
    }

    // if there is subsystems for the selected system, ask users to select the subsystems first
    if (selectedSystem.subsystems.length && !selectedSubsystem.optionId) {
      return (
        <>
          <Typography variant="h2" className={classes.title}>
            Select sub-system to be examined
          </Typography>
          {!isWidthSm ? (
            <div className={classes.gridListContainer}>
              <div style={{ height: 20 }} />
              <GridList
                spacing={20}
                cellHeight={100}
                cols={2}
                className={classes.rightGridList}
              >
                {selectedSystem.subsystems.map((subsystem) => (
                  <GridListTile key={subsystem.id}>
                    <CategoryButton
                      label={subsystem.name}
                      onClick={() => {
                        setSelectedSubsystem(subsystem);
                        setSelectedPart({
                          id: "",
                          name: "",
                        });
                      }}
                    />
                  </GridListTile>
                ))}
              </GridList>
            </div>
          ) : null}
        </>
      );
    }

    // for vitals, skip select body part stage
    if (selectedSystem.optionId === "examSystem_Vitals") {
      return (
        <div className={classes.vitals}>
          {isWidthSm ? (
            <CustomLabel
              arr={pickLabelArr("subsystem")}
              style={{ marginBottom: 10 }}
            />
          ) : null}
          <Typography variant="h2" className={classes.title}>
            Enter reading for Vitals
          </Typography>
          <TextField
            className={classes.searchfield}
            variant="outlined"
            margin="normal"
            size="small"
            fullWidth
            id="sign"
            placeholder="Enter reading"
            name="sign"
            onChange={(event) => {
              setSign(event.target.value);
            }}
            InputProps={{
              className: classes.searchinput,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={onAsk}>
                    <ArrowRightAltIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <div className={classes.vitalSignAns}>{renderAnswer()}</div>
          <AnswerLinkPopup linkArray={link} />
        </div>
      );
    } else {
      return (
        <>
          <div className={classes.labelContainer}>
            {isWidthSm ? (
              <CustomLabel
                arr={
                  selectedPart.id.length
                    ? selectedSubsystem.name
                      ? pickLabelArr("partWithSubsystem")
                      : pickLabelArr("partWithoutSubsystem")
                    : pickLabelArr("subsystem")
                }
              />
            ) : null}
          </div>
          <div className={classes.bodyContainer}>
            <div className={classes.bodySubContainer}>
              {selectedPart.id.length ? (
                <div className={classes.searchContainer}>
                  <Typography variant="h4" className={classes.subtitle}>
                    {`${selectedPart.name}: What are you looking for?`}
                  </Typography>
                  <div id="sign" className={classes.searchDiv}>
                    <TextField
                      className={classes.searchfield}
                      variant="outlined"
                      margin="normal"
                      size="small"
                      fullWidth
                      placeholder="Enter sign"
                      name="sign"
                      onChange={(event) => {
                        setSign(event.target.value);
                      }}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          onAsk();
                          e.preventDefault();
                        }
                      }}
                      InputProps={{
                        className: classes.searchinput,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={onAsk}>
                              <ArrowRightAltIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                  <div className={classes.signAns}>{renderAnswer()}</div>
                  <AnswerLinkPopup linkArray={link} />
                </div>
              ) : null}
            </div>
            {selectedPart.id.length ? null : (
              <div
                id="examinationBodyContainer"
                className={classes.bodyAndSlider}
              >
                <div
                  className={classes.magnifierDiv}
                  style={
                    width > 400
                      ? { minWidth: 300 }
                      : { width: width - 100, height: 90 }
                  }
                >
                  <p className={classes.magnifierTitle}>Magnification Ratio</p>
                  <div style={{ marginTop: -10 }}>
                    <Slider
                      orientation="horizontal"
                      value={magnifyRate}
                      onChange={handleMagnifyRateChange}
                      min={2}
                      max={5}
                      step={0.1}
                      valueLabelDisplay="auto"
                      marks={sliderMarks}
                    />
                  </div>
                </div>
                <div
                  className={classes.bodyDiv}
                  style={{
                    height: height - 310,
                    minHeight: !isWidthSm ? 305 : 0,
                    width:
                      width > 965
                        ? 500
                        : width > 840
                        ? 400
                        : width > 600
                        ? 320
                        : width - 70,
                  }}
                >
                  <ImageMagnifier
                    image={
                      gender === "male" ? malePatient_img : femalePatient_img
                    }
                    markImg={search_img}
                    markWidth={24}
                    markHeight={24}
                    onMarkClick={handleClick}
                    marks={parts}
                    style={{
                      width: 400,
                      margin: "auto",
                    }}
                    magnifyRate={magnifyRate}
                  />
                </div>
                <div>
                  <Button
                    variant="contained"
                    className={classes.generalBlue}
                    onClick={() => {
                      setSelectedPart(general);
                    }}
                    style={
                      isWidthSm ? { marginTop: 5, whiteSpace: "nowrap" } : {}
                    }
                    disabled={!general}
                  >
                    General Appearance
                  </Button>
                  <Button
                    variant="contained"
                    className={classes.generalBlue}
                    onClick={() => {
                      setSelectedPart(vital);
                    }}
                    style={
                      isWidthSm ? { marginTop: 5, whiteSpace: "nowrap" } : {}
                    }
                    disabled={!vital}
                  >
                    Vital Signs
                  </Button>
                </div>
              </div>
            )}
          </div>
        </>
      );
    }
  };

  // render subsystems for the selected system
  const renderSubsystems = (system) => {
    if (system.subsystems)
      return system.subsystems.map((subsystem) => (
        <CategoryButton
          mobile={!!isWidthSm}
          label={subsystem.name}
          onClick={() => {
            setSelectedSubsystem(subsystem);
            setSelectedPart({
              id: "",
              name: "",
            });
          }}
        />
      ));

    return null;
  };

  const renderLeftContent = () => {
    if (isWidthSm)
      return (
        <List
          id="examinationSystemList"
          className={classes.gridList}
          style={{ height: isWidthSm ? height - 300 : 400 }}
        >
          {systemList
            .filter(
              (s) =>
                !search || s.name.toLowerCase().includes(search.toLowerCase())
            )
            .map((system) => (
              <ListItem
                key={system.id}
                id={system.optionId}
                className={classes.listItem}
              >
                <Accordion
                  elevation={0}
                  expanded={selectedSystem.id === system.id}
                  className={classes.accordionStyle}
                >
                  <AccordionSummary className={classes.accordionSummary}>
                    <CategoryButton
                      mobile
                      disableRipple={true}
                      label={system.name}
                      onClick={() => {
                        //if user clicks on the same system type, the subsystem will collapse
                        setSelectedSubsystem({ id: "" });
                        selectedSystem.id === system.id
                          ? setSelectedSystem({ id: "" })
                          : setSelectedSystem(system);
                        setSelectedPart({
                          id: "",
                          name: "",
                        });
                        setSign("");
                        setAnswer("");
                        setLink([]);
                      }}
                      red={selectedSystem.id === system.id}
                      blue={selectedSystem.id !== system.id}
                    />
                  </AccordionSummary>
                  {system.subsystems && system.subsystems.length !== 0 ? (
                    <AccordionDetails>
                      <div className={classes.subSystem}>
                        <Typography variant="h2" className={classes.title}>
                          Select sub-system to be examined
                        </Typography>
                        {renderSubsystems(system)}
                      </div>
                    </AccordionDetails>
                  ) : null}
                </Accordion>
              </ListItem>
            ))}
          {document.getElementById(selectedSystem.optionId)
            ? document.getElementById(selectedSystem.optionId).scrollIntoView({
                behavior: "smooth",
                inline: "center",
                block: "center",
              })
            : null}
        </List>
      );

    return (
      <>
        <List
          id="examinationSystemList"
          className={classes.gridList}
          style={{ height: height - 300, minHeight: 320 }}
        >
          {systemList
            .filter(
              (s) =>
                !search || s.name.toLowerCase().includes(search.toLowerCase())
            )
            .map((system) => (
              <ListItem key={system.id} className={classes.listItem}>
                <CategoryButton
                  mobile={isWidthSm}
                  label={system.name}
                  onClick={() => {
                    setSelectedSubsystem({ id: "" });
                    setSelectedSystem(system);
                    setSelectedPart({
                      id: "",
                      name: "",
                    });
                    setSign("");
                    setAnswer("");
                    setLink([]);
                  }}
                  red={selectedSystem.id === system.id}
                  blue={selectedSystem.id !== system.id}
                />
              </ListItem>
            ))}
        </List>
      </>
    );
  };

  const handleClick = (markObj) => {
    setAnswer("");
    setLink([]);
    setSelectedPart({
      id: markObj.id,
      name: markObj.hoverText,
    });
  };

  const searchBar = () => {
    return (
      <TextField
        className={classes.examSearchfield}
        variant="outlined"
        margin="normal"
        size="medium"
        fullWidth
        id="search"
        placeholder="Search"
        name="search"
        onChange={(event) => {
          setSearch(event.target.value);
        }}
        InputProps={{
          className: classes.searchinput,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton>
                <SearchIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    );
  };

  const rightContentReady =
    (selectedSystem.id !== "" && selectedSystem.subsystems.length === 0) ||
    (selectedSystem.id !== "" && selectedSubsystem.id !== "");

  return (
    <React.Fragment>
      <CssBaseline />
      <GameModalLayout
        title={isWidthSm && rightContentReady ? null : "Examination"}
        left={
          <div style={{ flex: 9 }}>
            {!isWidthSm ? (
              <>
                {searchBar()}
                {renderLeftContent()}
              </>
            ) : rightContentReady ? (
              renderRightContent()
            ) : (
              <>
                {searchBar()}
                {!!selectedSystem.subsystems ? null : (
                  <div className={classes.mobileHeader}>
                    <Typography variant="h2" className={classes.title}>
                      Select system to be examined
                    </Typography>
                  </div>
                )}
                {renderLeftContent()}
              </>
            )}
          </div>
        }
        right={isWidthSm ? null : renderRightContent()}
      />
    </React.Fragment>
  );
}
